import {DateTime, Interval} from "luxon";
import {container, inject, singleton} from "tsyringe";
import {appState} from "../../app.state";
import {BackendClient} from "../../connectivity/backend-client";
import {QueryParameter} from "../../connectivity/query-parameter.interface";
import {persistence} from "../../decorators/persistence.decorator";
import {TransformerHelper} from "../../helpers/transformer.helper";
import {EntityState} from "../../persistence/entity-state.enum";
import {Repository} from "../../persistence/repository";
import {TaskListQueryItem} from "../../types/task-list-query.item.type";
import {IssueDTO} from "../dtos/issue.dto";
import {Issue} from "../entities/issue.entity";
import {IssueStatusRepository} from "./issue-status.repository";
import {IssueTypeRepository} from "./issue-type.repository";
import {IssueQuery} from "./issue.query";
import {OrderByDirection} from "./order-by-direction.enum";
import {
    ISSUE_STATUS_IN_PROGRESS,
    ISSUE_STATUS_OPEN,
    ISSUE_TYPE_MILESTONE,
    ISSUE_TYPE_MISCELLANEOUS,
    ISSUE_TYPE_PUBLIC_HOLIDAY,
    ISSUE_TYPE_REMINDER,
    ISSUE_TYPE_VACATION
} from "../../constants";
import {get} from "lit-translate";
import {Project} from "../entities/project.entity";
import {EntityObjectArray} from "../entity-object-array";

type RequestBody = {
    queryParams: QueryParameter<Issue>[],
    orderBy: Array<{
        key: string,
        order: 'ASC' | 'DESC'
    }>,
    limit: number,
    offset: number,
    searchTerm?: string,
    listMode: 'tasks' | 'absences' | 'all',
    includeDeleted?: boolean
};

@singleton()
@persistence({entity: Issue})
export class IssueRepository extends Repository<Issue> {

    public constructor(
        @inject(BackendClient) private readonly _backendClient: BackendClient
    ) {
        super();
    }

    public async createTimeFrame(board: Project, interval: Interval): Promise<Issue> {

        const issue = new Issue(
            (await container.resolve(IssueTypeRepository).findOneById(ISSUE_TYPE_MILESTONE))!,
            (await container.resolve(IssueStatusRepository).findOneById(ISSUE_STATUS_OPEN))!
        );
        issue.title = get(`general.milestone`);
        issue.creator = await appState.employee;
        issue.timeRange = interval;
        issue.project = board;

        this.entityManager.setLoadedEntity(issue, EntityState.NEW);

        return issue;
    }

    public async create(): Promise<Issue> {

        const issue = new Issue(
            (await container.resolve(IssueTypeRepository).findOneById(ISSUE_TYPE_MISCELLANEOUS))!,
            (await container.resolve(IssueStatusRepository).findOneById(ISSUE_STATUS_OPEN))!
        );
        issue.title = ``;
        issue.creator = await appState.employee;

        const start = DateTime.utc().startOf(`day`);
        issue.timeRange = Interval.fromDateTimes(
            start,
            start.plus({day: 1})
        );

        this.entityManager.setLoadedEntity(issue, EntityState.NEW);

        return issue;
    }

    /**
     * Issues overlapping `interval` (start ≤ interval end, end ≥ interval start), not deleted.
     * `extraQueryParams` narrow it further on the server, e.g. to the calendar project for
     * "Mein Kalender" (Anke, 2026-10-16).
     */
    public async findAllByInterval(interval: Interval, includes: string[] = [], extraQueryParams: QueryParameter<Issue>[] = []): Promise<Issue[]> {
        const queryParams: QueryParameter<Issue>[] = [
            {
                key: `start`,
                comparator: `lte`,
                value: interval.end!.toSQLDate()
            }, {
                key: `end`,
                comparator: `gte`,
                value: interval.start!.toSQLDate()
            }, {
                key: `deletedAt`,
                comparator: `eq`,
                value: null,
            },
            ...extraQueryParams,
        ];

        const orderBy = [{
            key: `project`,
            order: `ASC`
        }];

        return this._backendClient.post<IssueDTO[]>(
            `/api/v1/query/issue`,
            false,
            {
                queryParams,
                orderBy,
                limit: null,
                offset: 0,
                includes
            }
        )
            .then(issueDTOs => issueDTOs ?? [])
            .then(async issueDTOs => {
                const issues = [];
                for (const issueDTO of issueDTOs) {
                    issues.push(await TransformerHelper.fromDTO(issueDTO, Issue));
                }

                return issues;
            });
    }

    public async findAllByIntervalAndIssueType(interval: Interval, issueTypeUid: string, includes: string[] = []): Promise<Issue[]> {
        const queryParams: QueryParameter<Issue>[] = [{
            key: `start`,
            comparator: `gte`,
            value: interval.start!.toSQLDate()
        }, {
            key: `start`,
            comparator: `lt`,
            value: interval.end!.toSQLDate()
        }, {
            key: "type.uid",
            comparator: `eq`,
            value: issueTypeUid
        }, {
            key: `deletedAt`,
            comparator: "eq",
            value: null
        }];

        const orderBy = [{
            key: `start`,
            order: `ASC`
        }];

        return this._backendClient.post<IssueDTO[]>(
            `/api/v1/query/issue`,
            false,
            {
                queryParams,
                orderBy,
                limit: null,
                offset: 0,
                includes
            }
        )
            .then(issueDTOs => issueDTOs ?? [])
            .then(async issueDTOs => {
                const issues = [];
                for (const issueDTO of issueDTOs) {
                    issues.push(await TransformerHelper.fromDTO(issueDTO, Issue));
                }

                return issues;
            });
    }

    public async findAllByProjectUid(uid: string, withDeleted = false): Promise<EntityObjectArray<Issue>> {

        const query = new IssueQuery();

        query.addOrdersBy({
            key: `start`,
            order: OrderByDirection.ASC
        });

        query.addQueryParameters({
            key: `project.uid`,
            comparator: `eq`,
            value: uid
        }, {
            key: `type.uid`,
            comparator: `eq`,
            value: ISSUE_TYPE_MILESTONE
        });

        if (!withDeleted) {
            query.addQueryParameters({
                key: `deletedAt`,
                comparator: `eq`,
                value: null
            });
        }

        return this.query(query);
    }

    public async findAllFromNow(includes: string[]): Promise<Issue[]> {
        const queryParams: QueryParameter<Issue>[] = [{
            key: `start`,
            comparator: `gte`,
            value: DateTime.now().startOf(`day`).toSQLDate()
        }];

        const orderBy = [{
            key: `start`,
            order: `ASC`
        }]

        const issueDTOs = await this._backendClient.post<IssueDTO[]>(
            `/api/v1/query/issue`,
            false,
            {
                queryParams,
                orderBy,
                limit: 500,
                offset: 0,
                includes
            }
        );

        return Promise.all((issueDTOs ?? []).map(dto => TransformerHelper.fromDTO(dto, Issue)));
    }

    /**
     * Loads the uid list of the tasklist (or of the timeline's uid-set fetch with listMode 'all').
     *
     * With `includeDeleted` the backend also delivers soft-deleted issues, marked `isDeleted: true`
     * at the position they held before deletion — the tasklist needs them to locate a stored scroll
     * anchor whose issue was deleted since. The frontend's own "not deleted" condition is then left
     * out: while it is sent, the backend returns no deleted issue at all (measured 2026-10-01).
     */
    public async findAllUuidsByStatus(
        queryParams: QueryParameter<Issue>[],
        searchTerm: string | null = null,
        listMode: 'tasks' | 'absences' | 'all' = 'tasks',
        includeDeleted: boolean = false
    ): Promise<TaskListQueryItem[]> {
        if (!includeDeleted) {
            queryParams = [
                {
                    key: `deletedAt`,
                    comparator: "eq",
                    value: null
                }, ...queryParams
            ];
        }

        const orderBy: Array<{ key: string, order: 'ASC' | 'DESC' }> = [{
            key: `start`,
            order: `ASC`
        }]

        const requestBody: RequestBody = {
            queryParams,
            orderBy,
            limit: -1,
            offset: 0,
            listMode,
        }

        if (includeDeleted) {
            requestBody.includeDeleted = true;
        }

        if (searchTerm) {
            requestBody.searchTerm = searchTerm;
        }

        return (await this._backendClient.post<TaskListQueryItem[]>(
            `/api/v1/query/tasklist`,
            false,
            requestBody
        )) ?? [];
    }

    public async query(query: IssueQuery): Promise<EntityObjectArray<Issue>> {

        const issueDTOs = await this._backendClient.post<IssueDTO[]>(
            `/api/v1/query/issue`,
            false,
            query.requestBody
        );

        const issues: Array<Issue> = [];
        for (const issueDTO of issueDTOs ?? []) {
            issues.push(await TransformerHelper.fromDTO(issueDTO, Issue));
        }

        return new EntityObjectArray<Issue>(issues.length, issues);
    }

    public async findAllMilestonesByProject(uid: string): Promise<EntityObjectArray<Issue>> {
        const query = new IssueQuery();

        query.addQueryParameters({
            key: `project.uid`,
            comparator: `eq`,
            value: uid
        }, {
            key: `type.uid`,
            comparator: `eq`,
            value: ISSUE_TYPE_MILESTONE
        }, {
            key: `deletedAt`,
            comparator: `eq`,
            value: null
        });

        return this.query(query);
    }

    public async findAllUndone(): Promise<Issue[]> {
        const query = new IssueQuery();

        query.addQueryParameters({
            key: `status.uid`,
            comparator: `in`,
            value: [ISSUE_STATUS_OPEN, ISSUE_STATUS_IN_PROGRESS]
        }, {
            key: `deletedAt`,
            comparator: `eq`,
            value: null
        }, {
            key: `type.uid`,
            comparator: `neq`,
            value: ISSUE_TYPE_MILESTONE
        }, {
            key: `employeeToIssueAssignments.uid`,
            comparator: `neq`,
            value: null
        })

        return this.query(query);
    }

    /**
     * "My Organizer" mode's assigned-issues column (docs/feature-plans/0026-
     * personal-labels-issue-data/, follow-up UI step): every Issue assigned
     * to the CURRENTLY LOGGED-IN User's Employee (via
     * `employeeToIssueAssignments.employee.uid`, the same query key
     * `filter.helper.ts`'s `assignees` filter uses) that is NOT on DONE, and
     * NOT on REMINDER (Anke, 2026-09-08, bug report — then still with future REMINDER issues kept:
     * "bitte dort alle tickets anzeigen, die: 1. nicht auf 'Done' stehen UND
     * 2. nicht auf 'Reminder' mit einem Datum in der Vergangenheit... weil es
     * in manchen Projekten weitere Stati gibt und die werden im Moment nicht
     * angezeigt") — mirrors `findAllUndone()`'s shape (including its
     * Milestone exclusion — a Milestone is a timeline marker, not a work item
     * a User organizes personally, review-01 2026-09-06), just scoped to one
     * Employee.
     *
     * Deliberately a BLACKLIST (exclude DONE and REMINDER), not the ALLOWLIST (`status.uid eq OPEN`) this
     * used to be — a project can define its own additional statuses beyond the four fixed ones, and an
     * allowlist silently hid every one of them from this column; this was the actual bug.
     *
     * Since taxonomy step 7c (D23) it is one query: absences, holidays and meetings (the Absence,
     * Holiday and Reminder families) are left out by type, like Milestones, and with them the second
     * query for future REMINDER issues went away — what it returned were exactly those families. ToDos
     * that still carry REMINDER stay out until data migration 5a turns them into Reminder issues.
     *
     * Empty when the current User has no linked Employee (e.g. not fully
     * onboarded yet) — no query is even sent in that case, matching
     * `appState.employee`'s own `null` contract.
     *
     * Sorted by due date (`start`) ascending, then `title` ascending as a
     * tie-breaker (Anke, 2026-09-06: the unassigned column should read like a
     * simple due-date-ordered list).
     */
    public async findAllAssignedToMe(): Promise<Issue[]> {
        const employee = await appState.employee;
        if (!employee) {
            return [];
        }

        // D23 (taxonomy step 7c): no absences, holidays or meetings. The four families are excluded by
        // their roots — the backend widens a type filter on a root to its children (measured 2026-10-20) —
        // and DONE and REMINDER by status key, in lower case: the backend lowers the column but compares an
        // array value as sent, so upper-case keys would exclude nothing.
        const query = new IssueQuery();
        query.addQueryParameters({
            key: `employeeToIssueAssignments.employee.uid`,
            comparator: `eq`,
            value: employee.uid
        }, {
            key: `type.uid`,
            comparator: `nin`,
            value: [ISSUE_TYPE_MILESTONE, ISSUE_TYPE_VACATION, ISSUE_TYPE_PUBLIC_HOLIDAY, ISSUE_TYPE_REMINDER]
        }, {
            key: `deletedAt`,
            comparator: `eq`,
            value: null
        }, {
            key: `status.key`,
            comparator: `nin`,
            value: [`done`, `reminder`]
        });

        // A plain array: query() answers an EntityObjectArray, and sorting or mapping that subclass goes
        // through its own constructor, which callers here do not expect (the old merge spread into one too).
        return [...await this.query(query)].sort((a, b) =>
            a.start.toMillis() - b.start.toMillis() || a.title.localeCompare(b.title)
        );
    }
}
